import { useEffect, useRef } from "react";

/**
 * Lightweight canvas confetti burst — no dependencies.
 * Call the returned fire() to shoot confetti from the given origin element (or center).
 */
export function useConfetti() {
    const canvasRef = useRef(null);
    const particlesRef = useRef([]);
    const rafRef = useRef();

    useEffect(() => {
        const canvas = document.createElement("canvas");
        canvas.style.position = "fixed";
        canvas.style.inset = "0";
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        canvas.style.pointerEvents = "none";
        canvas.style.zIndex = "9999";
        canvas.width = window.innerWidth * 2;
        canvas.height = window.innerHeight * 2;
        canvas.getContext("2d").scale(2, 2);
        document.body.appendChild(canvas);
        canvasRef.current = canvas;

        const onResize = () => {
            canvas.width = window.innerWidth * 2;
            canvas.height = window.innerHeight * 2;
            canvas.getContext("2d").scale(2, 2);
        };
        window.addEventListener("resize", onResize);

        const draw = () => {
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const particles = particlesRef.current;
            for (let i = particles.length - 1; i >= 0; i--) {
                const p = particles[i];
                p.vy += 0.12; // gravity
                p.x += p.vx;
                p.y += p.vy;
                p.rot += p.vr;
                p.life -= 1;
                if (p.life <= 0 || p.y > window.innerHeight + 40) {
                    particles.splice(i, 1);
                    continue;
                }
                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rot);
                ctx.fillStyle = p.color;
                ctx.globalAlpha = Math.max(0, p.life / p.maxLife);
                ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
                ctx.restore();
            }
            if (particles.length > 0) {
                rafRef.current = requestAnimationFrame(draw);
            }
        };

        canvas.__fire = (originX, originY) => {
            const colors = ["#12C7A0", "#7C6BFF", "#FFF6D6", "#FFC93C", "#F472B6"];
            const N = 140;
            for (let i = 0; i < N; i++) {
                const angle = (-Math.PI / 2) + (Math.random() - 0.5) * Math.PI * 0.9;
                const speed = 6 + Math.random() * 8;
                particlesRef.current.push({
                    x: originX,
                    y: originY,
                    vx: Math.cos(angle) * speed + (Math.random() - 0.5) * 2,
                    vy: Math.sin(angle) * speed - 2,
                    w: 6 + Math.random() * 6,
                    h: 8 + Math.random() * 8,
                    rot: Math.random() * Math.PI,
                    vr: (Math.random() - 0.5) * 0.4,
                    color: colors[Math.floor(Math.random() * colors.length)],
                    life: 120 + Math.random() * 60,
                    maxLife: 180,
                });
            }
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(draw);
        };

        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener("resize", onResize);
            if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
        };
    }, []);

    const fire = (originX, originY) => {
        const canvas = canvasRef.current;
        if (canvas && canvas.__fire) {
            canvas.__fire(
                originX ?? window.innerWidth / 2,
                originY ?? window.innerHeight / 2,
            );
        }
    };

    return { fire };
}
